import { resolveExecutionAssetImageUrl } from "~/features/execution/activity-copy";
import {
  buildExecutionToastDetail,
  buildExecutionToastLabel,
  deriveExecutionToastStatus,
  isTerminalToastStatus,
  resolveExecutionToastAction,
  resolveExecutionToastDisplay,
  type ExecutionToastAction,
  type ExecutionToastStatus,
} from "../copy/execution-toast-copy";
import type { TrackedExecutionRecord } from "../tracking/record";

export type ExecutionToastTone = "progress" | "success" | "error" | "warning";

export interface ExecutionToastView {
  action: ExecutionToastAction;
  chainId: number | null;
  detail: string | null;
  iconSymbol: string | null;
  iconUrl: string | null;
  isTerminal: boolean;
  label: string;
  operationId: string;
  portfolioId: string;
  status: ExecutionToastStatus;
  terminalDurationMs: number;
  tokenIcons: Array<{ url: string; symbol: string }> | null;
  tone: ExecutionToastTone;
}

function toneForStatus(status: ExecutionToastStatus): ExecutionToastTone {
  switch (status) {
    case "success":
      return "success";
    case "error":
    case "cancelled":
      return "error";
    case "parked":
    case "action_required":
      return "warning";
    case "loading":
      return "progress";
  }
}

// Records are immutable per upsert, so view derivation (status/copy/refs
// parsing/icon resolution) is cached per record identity — one op streaming
// frames must not rebuild every other visible pill's view.
const viewCache = new WeakMap<TrackedExecutionRecord, ExecutionToastView>();

export function buildExecutionToastView(
  record: TrackedExecutionRecord,
): ExecutionToastView {
  const cached = viewCache.get(record);
  if (cached) {
    return cached;
  }
  const source = {
    displayContext: record.displayContext,
    handle: record.handle,
    liveOperation: record.liveOperation,
    summary: record.summary,
  };
  const status = deriveExecutionToastStatus(source);
  const action = resolveExecutionToastAction(source);
  const display = resolveExecutionToastDisplay(source);

  const iconUrl =
    display.tokenIconUrl ??
    (display.assetId
      ? resolveExecutionAssetImageUrl({ assetId: display.assetId })
      : null);

  const view: ExecutionToastView = {
    action,
    chainId: display.chainId,
    detail: buildExecutionToastDetail(status, source),
    iconSymbol: display.symbol,
    iconUrl,
    isTerminal: isTerminalToastStatus(status),
    label: buildExecutionToastLabel(status, action, display),
    operationId: record.operationId,
    portfolioId: record.handle.portfolioId,
    status,
    terminalDurationMs: record.terminalDurationMs,
    tokenIcons: display.tokenIcons,
    tone: toneForStatus(status),
  };
  viewCache.set(record, view);
  return view;
}

/** Newest operation first — index 0 is the frontmost pill in the stack. */
export function sortExecutionToastViews(
  records: readonly TrackedExecutionRecord[],
): TrackedExecutionRecord[] {
  return [...records].sort(
    (left, right) =>
      Date.parse(right.handle.createdAt) - Date.parse(left.handle.createdAt),
  );
}

/** One entry in the center stack — execution pills and simple pills share it
 * (v1 parity: all toasts live in the bottom-center stack). */
export type CenterToastItem =
  | {
      createdAtMs: number;
      key: string;
      kind: "execution";
      view: ExecutionToastView;
    }
  | {
      createdAtMs: number;
      key: string;
      kind: "simple";
      record: import("../simple-toast-store").SimpleToastRecord;
    };
